/**!
 * **music21j**: JavaScript reimplementation of Core music21 features.
 *
 * See https://www.trecento.com/music21docs/ for more details.
 *
 * Copyright (c) 2013-2026, Michael Scott Asato Cuthbert
 * Released under a BSD-3-clause license
 *
 */
/**
 *
 * Based on music21, Copyright (c) 2006-2026, Michael Scott Asato Cuthbert
 * The plan is to implement all core music21 features as Javascript and to expose
 * more sophisticated features via server-side connections to remote servers running the
 * python music21 (music21p).
 *
 * Requires a browser updated in the last 30 months (Baseline), naturally w/ SVG and Canvas.
 * Any recent version of Firefox, Safari, Edge, Chrome, etc. will do.
 *
 * All interfaces are beta and may change radically from day to day and release to release.
 * Do not use this in production code yet unless you are willing to pin to a release or change.
 *
 * Cuthbert acknowledges VexFlow and MIDI.js in particular for their great efforts without which
 * this library would not be possible.
 *
 */

import * as MIDI from 'midicube';  // to be removed when export * from is okay.
import * as Vex from 'vexflow';

// order below doesn't matter, but good to give a sense
// of what will be needed by almost everyone, and then
// alphabetical.

import * as exceptions21 from './exceptions21';
import * as prebase from './prebase';
import * as base from './base';
import * as common from './common';

import * as articulations from './articulations';
import * as audioRecording from './audioRecording';
import * as audioSearch from './audioSearch';
import * as bar from './bar';
import * as beam from './beam';
import * as chord from './chord';
import * as chordTables from './chordTables';
import * as clef from './clef';
import * as converter from './converter';
import defaults from './defaults';
import * as derivation from './derivation';
import * as duration from './duration';
import * as dynamics from './dynamics';
import * as editorial from './editorial';
import * as expressions from './expressions';
import * as figuredBass from './figuredBass';
import * as fromPython from './fromPython';
import * as harmony from './harmony';
import * as instrument from './instrument';
import * as interval from './interval';
import * as key from './key';
import * as keyboard from './keyboard';
import * as layout from './layout';
import * as metadata from './metadata';
import * as meter from './meter';
import * as miditools from './miditools';
import * as musicxml from './musicxml';
import * as note from './note';
import * as parseLoader from './parseLoader';
import * as pitch from './pitch';
import * as renderOptions from './renderOptions';
import * as roman from './roman';
import * as scale from './scale';
import * as sites from './sites';
import * as stream from './stream';
import * as style from './style';
import * as svgs from './svgs';
import * as tempo from './tempo';
import * as tie from './tie';
import * as tinyNotation from './tinyNotation';
import * as vfShims from './vfShims';
import * as vfShow from './vfShow';
import * as voiceLeading from './voiceLeading';
import * as webmidi from './webmidi';

import { debug } from './debug';

export const VERSION = '0.21.3';

export {
    MIDI,
    Vex,

    exceptions21,
    base,
    prebase,
    common,

    debug,

    articulations,
    audioRecording,
    audioSearch,
    bar,
    beam,
    chord,
    chordTables,
    clef,
    converter,
    defaults,
    derivation,
    duration,
    dynamics,
    editorial,
    expressions,
    figuredBass,
    fromPython,
    harmony,
    instrument,
    interval,
    key,
    keyboard,
    layout,
    metadata,
    meter,
    miditools,
    musicxml,
    note,
    parseLoader,
    pitch,
    renderOptions,
    roman,
    scale,
    sites,
    stream,
    style,
    svgs,
    tempo,
    tie,
    tinyNotation,
    vfShims,
    vfShow,
    voiceLeading,
    webmidi,
};

parseLoader.runConfiguration();
